import { useMachine } from '@xstate/react';
import { useCallback, useEffect, useMemo } from 'react';
import { LoadingOverlay } from '../screens/LoadingOverlay/LoadingOverlay';
import { ProviderGroupPicker } from '../screens/ProviderGroupPicker/ProviderGroupPicker';
import {
  FinancialInstitutionPicker,
  getSearchProvidersStrategy,
} from '../screens/FinancialInstitutionPicker/FinancialInstitutionPicker';
import { AuthenticationTypePicker } from '../screens/AuthenticationTypePicker/AuthenticationTypePicker';
import { AccessTypePicker } from '../screens/AccessTypePicker/AccessTypePicker';
import { GermanStates, ProviderEntity } from '../../models/provider';
import { CredentialsTypePicker } from '../screens/CredentialsTypePicker/CredentialsTypePicker';
import { useUrlQuery } from '../../components/ApplicationProviders/UrlQueryProvider/UrlQueryProvider';
import { DisplayDescriptionPicker } from '../screens/DisplayDescriptionPicker/DisplayDescriptionPicker';
import { FinancialServicePicker } from '../screens/FinancialServicePicker/FinancialServicePicker';
import { applicationParameters } from '../../utilities/application-parameters';
import { useConfiguration } from '../../components/ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { CommonError } from '../../stores/app-state-errors';
import {
  hasDemobankScenariosEnabled,
  shouldFallbackToProviderSelection,
} from '../../models/configuration';
import { UnstableProviderWarning } from '../../components/screens/ProviderTemporaryDisabled/UnstableProviderWarning';
import { useAppSelector } from '../../stores/redux-store';
import { getNormalizedProviderName } from '../../utilities/provider-view-set';
import { DemobankScenarioPicker } from '../screens/DemobankScenarioPicker/DemobankScenarioPicker';
import { useDemoBankScenarioProvider } from '../../components/ApplicationProviders/DemoBankScenarioProvider/DemoBankScenarioProvider';
import { useProduct } from '../../components/ApplicationProviders/ProductProvider/ProductProvider';
import { PageView, UserInteractionEvent } from '../../models/analytics';
import { useAnalytics } from '../Analytics/AnalyticsProvider';
import { RegionPicker } from '../screens/RegionPicker/RegionPicker';
import { createProviderSelectionMachine, getFilteredProviders } from './providerSelectionMachine';
import { useRegionSelection } from './useRegionSelection';

type ProviderSelectionMachineViewProps = {
  onProviderNotFound: ({ error }: { error: CommonError }) => void;
  onProviderSelected: ({ provider }: { provider: ProviderEntity }) => void;
  initialProviderName?: string;
  enableUnstableProviderWarning?: boolean;
  version: string;
};

// istanbul ignore next: this component is a switch.
export const ProviderSelectionMachineView = ({
  onProviderSelected,
  onProviderNotFound,
  initialProviderName,
  enableUnstableProviderWarning = false,
  version,
}: ProviderSelectionMachineViewProps) => {
  const selectedFinancialInstitutionId = applicationParameters.get('financialInstitutionId');
  const { market, locale } = useUrlQuery();
  const { productType } = useProduct();
  const { data: configurationData } = useConfiguration();
  const fallbackToProviderSelection = shouldFallbackToProviderSelection(configurationData);
  const isDemobankScenariosEnabled = hasDemobankScenariosEnabled(configurationData);
  const initialProviders = useAppSelector((state) => state.providers.data);
  const { setSelectedDemoBankScenario } = useDemoBankScenarioProvider();
  const { tinklinkAnalytics } = useAnalytics();
  const { isInTestGroup } = useRegionSelection();

  const isRegionSelectionEnabled = useCallback(
    (providerGroupName: string) => providerGroupName === 'Sparkassen' && isInTestGroup(),
    [isInTestGroup]
  );

  const machine = useMemo(
    () =>
      createProviderSelectionMachine({
        market,
        locale,
        selectedFinancialInstitutionId,
        fallbackToProviderSelection,
        enableUnstableProviderWarning,
        initialProviderName,
        initialProviders,
        version,
        isDemobankScenariosEnabled,
        isRegionSelectionEnabled,
        productType,
      }),
    [
      market,
      locale,
      selectedFinancialInstitutionId,
      fallbackToProviderSelection,
      enableUnstableProviderWarning,
      initialProviderName,
      initialProviders,
      version,
      isDemobankScenariosEnabled,
      isRegionSelectionEnabled,
      productType,
    ]
  );

  const [current, send] = useMachine(machine, { devTools: true });

  const isDone = current.matches('done');
  const isError = current.matches('error');

  const { selectedDemobankScenario } = current.context;

  useEffect(() => {
    if (isDone) {
      if (selectedDemobankScenario) {
        setSelectedDemoBankScenario(selectedDemobankScenario);
      }

      onProviderSelected({
        provider: current.context.selectedProvider!,
      });
    }
  }, [
    selectedDemobankScenario,
    current.context.filters,
    current.context.selectedProvider,
    isDone,
    onProviderSelected,
    setSelectedDemoBankScenario,
  ]);

  useEffect(() => {
    if (isError) {
      onProviderNotFound({ error: current.context.error! });
    }
  }, [current.context.error, isError, onProviderNotFound]);

  if (current.matches('done')) {
    return null;
  }

  if (current.matches('showSelectGroupDisplayNamePicker')) {
    return (
      <ProviderGroupPicker
        showProviderMissingError={current.context.providerMissing}
        providers={current.context.providers}
        onProviderViewSetClick={({ providerViewSetGroup }) => {
          const groupDisplayName = providerViewSetGroup.title;

          tinklinkAnalytics.sendInteractionEvent({
            view: PageView.PROVIDER_GROUP_SELECTION_SCREEN,
            action: `${PageView.PROVIDER_GROUP_SELECTION_SCREEN}/${UserInteractionEvent.LIST_ITEM}`,
            label: groupDisplayName,
          });

          send('SELECT_GROUP_DISPLAY_NAME', { groupDisplayName });
        }}
        onProviderItemClick={({ providerViewSetGroup }) => {
          const displayName = providerViewSetGroup.title;

          tinklinkAnalytics.sendInteractionEvent({
            view: PageView.PROVIDER_GROUP_SELECTION_SCREEN,
            action: `${PageView.PROVIDER_GROUP_SELECTION_SCREEN}/${UserInteractionEvent.LIST_ITEM}`,
            label: displayName,
          });

          send('SELECT_DISPLAY_NAME', { displayName });
        }}
      />
    );
  }

  if (current.matches('showFinancialInstitutionPicker')) {
    const providers = getFilteredProviders(current.context);
    const { regionName } = current.context.filters;
    const { regionMappings } = current.context;
    const searchIndividualProviders = getSearchProvidersStrategy(regionName, regionMappings);

    return (
      <FinancialInstitutionPicker
        onBack={() => {
          if (current.context.filters.regionName) {
            send('SHOW_REGION_PICKER');
          } else {
            send('RESET_FILTERS');
          }
        }}
        providers={providers}
        searchIndividualProviders={searchIndividualProviders}
        groupDisplayName={getNormalizedProviderName(providers[0])}
        onProviderItemClick={({ providerViewSetGroup }) => {
          const financialInstitutionId = providerViewSetGroup.financialInstitutionIds[0];

          tinklinkAnalytics.sendInteractionEvent({
            view: PageView.PROVIDER_FINANCIAL_SELECTION_SCREEN,
            action: `${PageView.PROVIDER_FINANCIAL_SELECTION_SCREEN}/${UserInteractionEvent.LIST_ITEM}`,
            label: financialInstitutionId,
          });

          send('SELECT_FINANCIAL_INSTITUTION', {
            financialInstitutionId,
          });
        }}
      />
    );
  }

  if (current.matches('showSelectAuthenticationUserType')) {
    const providers = getFilteredProviders(current.context);
    const { financialInstitutionId } = providers[0];

    return (
      <AuthenticationTypePicker
        onBack={() => {
          send('RESET_FILTERS');
        }}
        providers={providers}
        financialInstitutionId={financialInstitutionId}
        onProviderItemClick={({ authenticationUserType }) => {
          tinklinkAnalytics.sendInteractionEvent({
            view: PageView.PROVIDER_AUTHENTICATION_SELECTION_SCREEN,
            action: `${PageView.PROVIDER_AUTHENTICATION_SELECTION_SCREEN}/${UserInteractionEvent.LIST_ITEM}`,
            label: authenticationUserType,
          });

          send('SELECT_AUTHENTICATION_USER_TYPE', {
            authenticationUserType,
          });
        }}
      />
    );
  }

  if (current.matches('showAccessTypePicker')) {
    const providers = getFilteredProviders(current.context);
    const { financialInstitutionId } = providers[0];
    const { authenticationUserType } = providers[0];

    return (
      <AccessTypePicker
        onBack={() => {
          send('RESET_FILTERS');
        }}
        providers={providers}
        authenticationUserType={authenticationUserType}
        financialInstitutionId={financialInstitutionId}
        onProviderItemClick={({ accessType }) => {
          tinklinkAnalytics.sendInteractionEvent({
            view: PageView.PROVIDER_ACCESS_SELECTION_SCREEN,
            action: `${PageView.PROVIDER_ACCESS_SELECTION_SCREEN}/${UserInteractionEvent.LIST_ITEM}`,
            label: accessType,
          });

          send('SELECT_ACCESS_TYPE', { accessType });
        }}
      />
    );
  }

  if (current.matches('showCredentialsTypePicker')) {
    const providers = getFilteredProviders(current.context);

    return (
      <CredentialsTypePicker
        providers={providers}
        onBack={() => {
          if (selectedFinancialInstitutionId) {
            send('RESET_FINANCIAL_SERVICES_DATA');
          } else {
            send('RESET_FILTERS');
          }
        }}
        onProviderItemClick={({ credentialsType }) => {
          tinklinkAnalytics.sendInteractionEvent({
            view: PageView.PROVIDER_CREDENTIALS_SELECTION_SCREEN,
            action: `${PageView.PROVIDER_CREDENTIALS_SELECTION_SCREEN}/${UserInteractionEvent.LIST_ITEM}`,
            label: credentialsType,
          });

          send('SELECT_CREDENTIALS_TYPE', { credentialsType });
        }}
      />
    );
  }

  if (current.matches('showDisplayDescriptionPicker')) {
    const providers = getFilteredProviders(current.context);

    return (
      <DisplayDescriptionPicker
        providers={providers}
        onBack={() => {
          send('RESET_FILTERS');
        }}
        onProviderItemClick={({ providerName }) => {
          tinklinkAnalytics.sendInteractionEvent({
            view: PageView.PROVIDER_DESCRIPTION_SELECTION_SCREEN,
            action: `${PageView.PROVIDER_DESCRIPTION_SELECTION_SCREEN}/${UserInteractionEvent.LIST_ITEM}`,
            label: providerName,
          });

          send('SELECT_PROVIDER_NAME', { providerName });
        }}
      />
    );
  }

  if (current.matches('showFinancialServicePicker')) {
    const providers = getFilteredProviders(current.context);

    return (
      <FinancialServicePicker
        providers={providers}
        onBack={() => {
          // TODO: this component should not display back button
        }}
        onProviderItemClick={({ accessType, financialServicesSegment }) => {
          tinklinkAnalytics.sendInteractionEvent({
            view: PageView.PROVIDER_SEGMENT_SELECTION_SCREEN,
            action: `${PageView.PROVIDER_SEGMENT_SELECTION_SCREEN}/${UserInteractionEvent.LIST_ITEM}`,
            label: `${accessType}/${financialServicesSegment}`,
          });

          send('SELECT_FINANCIAL_SERVICE', { accessType, financialServicesSegment });
        }}
      />
    );
  }

  if (current.matches('showProviderUnstableScreen')) {
    return (
      <UnstableProviderWarning
        provider={current.context.selectedProvider!}
        onBack={() => {
          send('RESET_FILTERS');
        }}
        onClose={() => {
          send('UNSTABLE_PROVIDER_ACKNOWLEDGED');
        }}
        onUserCancel={() => {
          send('USER_CANCELLED');
        }}
      />
    );
  }

  if (current.matches('showRegionPicker')) {
    const providers = getFilteredProviders(current.context);
    const iconUrl = providers.length > 0 ? providers[0].images.icon : '';

    return (
      <RegionPicker
        states={Object.values(GermanStates)}
        groupDisplayName={getNormalizedProviderName(providers[0])}
        onSelectState={(state) => {
          tinklinkAnalytics.sendInteractionEvent({
            view: PageView.PROVIDER_REGION_PICKER_SCREEN,
            action: `${PageView.PROVIDER_REGION_PICKER_SCREEN}/${UserInteractionEvent.LIST_ITEM}`,
            label: state,
          });
          send('SELECT_REGION_PICKER', { regionName: state });
        }}
        onBack={() => {
          send('RESET_FILTERS');
        }}
        iconUrl={iconUrl}
      />
    );
  }

  if (current.matches('showDemobankScenarioPicker')) {
    return (
      <DemobankScenarioPicker
        scenarios={current.context.demobankScenarios}
        onBack={() => {
          send('RESET_FILTERS');
        }}
        onNoScenario={() => {
          send('SELECT_NO_SCENARIO');
        }}
        onClickScenario={(scenarioId) => {
          tinklinkAnalytics.sendInteractionEvent({
            view: PageView.PROVIDER_DEMOBANK_SELECTION_SCREEN,
            action: `${PageView.PROVIDER_DEMOBANK_SELECTION_SCREEN}/${UserInteractionEvent.LIST_ITEM}`,
            label: scenarioId,
          });

          send('SELECT_DEMOBANK_SCENARIO', { scenarioId });
        }}
      />
    );
  }

  return <LoadingOverlay />;
};
